import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDataGrid from "@inovua/reactdatagrid-community";
import "@inovua/reactdatagrid-community/index.css";
import SelectEditor from "@inovua/reactdatagrid-community/SelectEditor";
import BoolEditor from "@inovua/reactdatagrid-community/BoolEditor";
import NumericEditor from "@inovua/reactdatagrid-community/NumericEditor";
import dayjs, { type Dayjs } from "dayjs";
import type { TypeCellSelection, TypeColumn, TypeComputedProps, TypeEditInfo } from "@inovua/reactdatagrid-community/types";
import { DatePicker, Flex, Modal, message } from "antd";
import styled from "@emotion/styled";
import type { ProductItemIntegrationResponse } from "apiClients/base-generated";
import { useLocaleStore } from "stores/localeStore";

/** Max antal rader som laddas i redigeraren åt gången (prestanda). */
export const NORTHMILL_SYNC_EDIT_MAX_RECORDS = 200;

/** Default kontokod i Northmill när integrationssynken saknar `northmillAccountCodeId`. */
const DEFAULT_NORTHMILL_ACCOUNT_CODE_ID = "029e74dc-f8f5-4cfb-a3be-24f77217f9e9";

/** Endast kontokoder vars visningsnamn innehåller detta (API-listan filtreras; befintlig rad + standard läggs ändå till). */
const ACCOUNT_CODE_OPTION_NAME_CONTAINS = "varor";

const GridModalShell = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  .inovua-react-toolkit-menu__submenu-wrapper {
    z-index: 1200;
  }
`;

/** Undviker Inovua `DateEditor` som kräver `moment`; Ant Design + dayjs används redan i projektet. */
function NorthmillGridDateEditor(props: {
  value?: Date | string | null;
  onChange?: (value: Date | null) => void;
  onComplete?: () => void;
  autoFocus?: boolean;
}) {
  const { value, onChange, onComplete, autoFocus } = props;
  const pickerValue = useMemo((): Dayjs | null => {
    if (value == null) return null;
    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : dayjs(value);
    }
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }, [value]);

  return (
    <div className="InovuaReactDataGrid__cell__editor" style={{ padding: 4, minWidth: 140 }}>
      <DatePicker
        autoFocus={autoFocus}
        style={{ width: "100%" }}
        format="YYYY-MM-DD"
        allowClear
        value={pickerValue}
        onChange={(d) => onChange?.(d && d.isValid() ? d.toDate() : null)}
        onOpenChange={(open) => {
          if (!open) onComplete?.();
        }}
      />
    </div>
  );
}

export type NorthmillGroupSelectOption = {
  value: string;
  label: string;
};

/** En rad som skickats till API (endast ändrade rader vid spara). */
export type NorthmillSyncEditRowSave = {
  integrationSyncId: number;
  itemNo: string;
  northmillGroupId: string | null;
  northmillAccountCodeId: string | null;
  isInactive: boolean;
  lockedUntil: string | null;
  northmillLockedQuantity: number | null;
};

export type IntegrationNorthmillSyncEditModalProps = {
  open: boolean;
  records: ProductItemIntegrationResponse[];
  totalSelectedBeforeCap?: number;
  groupOptions: NorthmillGroupSelectOption[];
  groupsLoading?: boolean;
  accountCodeOptions: NorthmillGroupSelectOption[];
  accountCodesLoading?: boolean;
  onCancel: () => void;
  onSave: (changedRows: NorthmillSyncEditRowSave[]) => Promise<void>;
  confirmLoading?: boolean;
};

type NorthmillGridRow = {
  id: number;
  productName: string;
  itemNo: string;
  northmillGroupId: string;
  northmillAccountCodeId: string;
  isInactive: boolean;
  lockedUntil: Date | null;
  northmillLockedQuantity: number | null;
};

function productTitle(record: ProductItemIntegrationResponse, locale: string | undefined): string {
  return record.fullDisplayName ?? "—";
}

function rowToSavePayload(r: NorthmillGridRow): NorthmillSyncEditRowSave {
  const itemNo = r.itemNo?.trim();
  if (!itemNo) {
    throw new Error("Saknar artikelnummer");
  }
  return {
    integrationSyncId: r.id,
    itemNo,
    northmillGroupId: r.northmillGroupId.trim() || null,
    northmillAccountCodeId: r.northmillAccountCodeId.trim() || null,
    isInactive: r.isInactive,
    lockedUntil: r.lockedUntil ? dayjs(r.lockedUntil).endOf("day").toISOString() : null,
    northmillLockedQuantity:
      r.northmillLockedQuantity != null && !Number.isNaN(r.northmillLockedQuantity)
        ? r.northmillLockedQuantity
        : null,
  };
}

function payloadsEqual(a: NorthmillSyncEditRowSave, b: NorthmillSyncEditRowSave): boolean {
  return (
    a.integrationSyncId === b.integrationSyncId &&
    a.itemNo === b.itemNo &&
    a.northmillGroupId === b.northmillGroupId &&
    a.northmillAccountCodeId === b.northmillAccountCodeId &&
    a.isInactive === b.isInactive &&
    a.lockedUntil === b.lockedUntil &&
    a.northmillLockedQuantity === b.northmillLockedQuantity
  );
}

function recordsToGridRows(
  records: ProductItemIntegrationResponse[],
  locale: string | undefined,
): NorthmillGridRow[] {
  return records.map((r) => {
    const syncId = r.id;
    if (syncId == null) {
      throw new Error("Saknar integrationssynk-ID");
    }
    const itemNo = r.itemNo?.trim();
    if (!itemNo) {
      throw new Error("Saknar artikelnummer");
    }
    const gid = r.northmillGroupId?.trim() ?? "";
    let aid = r.northmillAccountCodeId?.trim() ?? "";
    if (!aid) {
      aid = DEFAULT_NORTHMILL_ACCOUNT_CODE_ID;
    }
    return {
      id: syncId,
      productName: productTitle(r, locale),
      itemNo,
      northmillGroupId: gid,
      northmillAccountCodeId: aid,
      isInactive: Boolean(r.isInactive),
      lockedUntil: r.lockedUntil ? dayjs(r.lockedUntil).toDate() : null,
      northmillLockedQuantity: r.northmillLockedQuantity ?? null,
    };
  });
}

export function IntegrationNorthmillSyncEditModal({
  open,
  records,
  totalSelectedBeforeCap,
  groupOptions,
  groupsLoading: _groupsLoading = false,
  accountCodeOptions,
  accountCodesLoading: _accountCodesLoading = false,
  onCancel,
  onSave,
  confirmLoading = false,
}: IntegrationNorthmillSyncEditModalProps) {
  const activeLocale = useLocaleStore((s) => s.activeLanguage?.locale);
  const [gridRows, setGridRows] = useState<NorthmillGridRow[]>([]);
  const [cellSelection, setCellSelection] = useState<TypeCellSelection>({});
  const initialPayloadsRef = useRef<NorthmillSyncEditRowSave[]>([]);
  const gridApiRef = useRef<TypeComputedProps | null>(null);

  const groupSelectList = useMemo(() => {
    const byId = new Map<string, { id: string; label: string }>();
    for (const o of groupOptions) {
      byId.set(o.value, { id: o.value, label: o.label });
    }
    for (const r of records) {
      const gid = r.northmillGroupId?.trim();
      if (!gid) continue;
      if (!byId.has(gid)) {
        byId.set(gid, { id: gid, label: `${gid} (saknas i grupplistan)` });
      }
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, "sv"));
  }, [groupOptions, records]);

  const accountCodeSelectList = useMemo(() => {
    const byId = new Map<string, { id: string; label: string }>();
    for (const o of accountCodeOptions) {
      const name = (o.label ?? "").toLowerCase();
      if (!name.includes(ACCOUNT_CODE_OPTION_NAME_CONTAINS)) continue;
      byId.set(o.value, { id: o.value, label: o.label });
    }
    for (const r of records) {
      const aid = r.northmillAccountCodeId?.trim();
      if (!aid) continue;
      if (!byId.has(aid)) {
        byId.set(aid, { id: aid, label: `${aid} (saknas i kontokodslistan)` });
      }
    }
    if (!byId.has(DEFAULT_NORTHMILL_ACCOUNT_CODE_ID)) {
      byId.set(DEFAULT_NORTHMILL_ACCOUNT_CODE_ID, {
        id: DEFAULT_NORTHMILL_ACCOUNT_CODE_ID,
        label: `Varor 25% moms (${DEFAULT_NORTHMILL_ACCOUNT_CODE_ID})`,
      });
    }
    return [...byId.values()].sort((a, b) => a.label.localeCompare(b.label, "sv"));
  }, [accountCodeOptions, records]);

  const columns = useMemo<TypeColumn[]>(
    () => [
      {
        name: "productName",
        header: "Produktnamn",
        type: "string",
        editable: false,
        cellSelectable: false,
        defaultFlex: 2.2,
        minWidth: 160,
      },
      {
        name: "itemNo",
        header: "Art.nr",
        type: "string",
        editable: false,
        cellSelectable: false,
        defaultFlex: 0.9,
        minWidth: 88,
      },
      {
        name: "northmillGroupId",
        header: "Grupp (Northmill)",
        type: "string",
        editable: true,
        defaultFlex: 1.4,
        minWidth: 140,
        editor: SelectEditor as any,
        editorProps: {
          idProperty: "id",
          dataSource: groupSelectList,
          collapseOnSelect: true,
          clearIcon: null,
        },
        render: ({ value }: { value: string }) => {
          if (!value?.trim()) return "—";
          const hit = groupSelectList.find((x) => x.id === value);
          return hit?.label ?? value;
        },
      },
      {
        name: "northmillAccountCodeId",
        header: "Kontokod",
        type: "string",
        editable: true,
        defaultFlex: 1.6,
        minWidth: 160,
        editor: SelectEditor as any,
        editorProps: {
          idProperty: "id",
          dataSource: accountCodeSelectList,
          collapseOnSelect: true,
          clearIcon: null,
        },
        render: ({ value }: { value: string }) => {
          if (!value?.trim()) return "—";
          const hit = accountCodeSelectList.find((x) => x.id === value);
          return hit?.label ?? value;
        },
      },
      {
        name: "isInactive",
        header: "Dold (Northmill)",
        type: "boolean",
        editable: true,
        defaultFlex: 0.75,
        minWidth: 120,
        editor: BoolEditor as any,
        render: ({ value }: { value: boolean }) => (value ? "Ja" : "Nej"),
      },
      {
        name: "lockedUntil",
        header: "Synk pausad t.o.m.",
        editable: true,
        defaultFlex: 1,
        minWidth: 140,
        editor: NorthmillGridDateEditor as any,
        render: ({ value }: { value: Date | string | null }) => {
          if (value == null) return "—";
          const d = value instanceof Date ? value : dayjs(value).toDate();
          return Number.isNaN(d.getTime()) ? "—" : dayjs(d).format("YYYY-MM-DD");
        },
      },
      {
        name: "northmillLockedQuantity",
        header: "Pausat lagerantal",
        type: "number",
        editable: true,
        defaultFlex: 0.85,
        minWidth: 120,
        editor: NumericEditor as any,
        render: ({ value }: { value: number | null | undefined }) =>
          value == null || Number.isNaN(value) ? "—" : String(value),
      },
    ],
    [groupSelectList, accountCodeSelectList],
  );

  const resetGridFromRecords = useCallback(() => {
    setCellSelection({});
    gridApiRef.current = null;
    if (!records.length) {
      setGridRows([]);
      initialPayloadsRef.current = [];
      return;
    }
    try {
      const rows = recordsToGridRows(records, activeLocale);
      setGridRows(rows);
      initialPayloadsRef.current = rows.map(rowToSavePayload);
    } catch {
      setGridRows([]);
      initialPayloadsRef.current = [];
    }
  }, [records, activeLocale]);

  const onCellSelectionChange = useCallback((value: { [key: string]: boolean }) => {
    setCellSelection(value);
  }, []);

  /** Håller React `dataSource` i sync med gridens cache (t.ex. Excel-fill / bulk update) så vi inte skriver över ändringar vid rerender. */
  const onDataSourceCacheChange = useCallback(() => {
    queueMicrotask(() => {
      const d = gridApiRef.current?.getData?.();
      if (Array.isArray(d) && d.length) {
        setGridRows(d as NorthmillGridRow[]);
      }
    });
  }, []);

  useEffect(() => {
    if (open && records.length) {
      resetGridFromRecords();
    }
  }, [open, records, resetGridFromRecords]);

  const onEditComplete = useCallback((editInfo: TypeEditInfo) => {
    setGridRows((prev) => {
      const next = [...prev];
      const rid = editInfo.rowId as number | string;
      const idx = next.findIndex((row) => row.id === rid || String(row.id) === String(rid));
      if (idx < 0) return prev;
      const col = editInfo.columnId as keyof NorthmillGridRow;
      if (
        col !== "northmillGroupId" &&
        col !== "northmillAccountCodeId" &&
        col !== "isInactive" &&
        col !== "lockedUntil" &&
        col !== "northmillLockedQuantity"
      ) {
        return prev;
      }
      let v: unknown = editInfo.value;
      if (col === "northmillLockedQuantity") {
        if (v === "" || v == null) v = null;
        else if (typeof v === "number") v = Number.isNaN(v) ? null : v;
        else if (typeof v === "string") {
          const n = Number(v);
          v = v.trim() === "" || Number.isNaN(n) ? null : n;
        }
      }
      if (col === "lockedUntil" && v != null && !(v instanceof Date) && typeof v === "string") {
        const parsed = dayjs(v);
        v = parsed.isValid() ? parsed.toDate() : null;
      }
      next[idx] = { ...next[idx], [col]: v as never };
      return next;
    });
  }, []);

  const isCapped = totalSelectedBeforeCap != null && totalSelectedBeforeCap > records.length;
  const title =
    records.length <= 1
      ? "Redigera Northmill Artiklar"
      : isCapped
        ? `Redigera Northmill Artiklar (${records.length} av ${totalSelectedBeforeCap} artiklar)`
        : `Redigera Northmill Artiklar (${records.length} artiklar)`;

  const syncIds = records.map((r) => r.id).filter((id): id is number => id != null);

  const resolveRowsForSave = useCallback((): NorthmillGridRow[] => {
    const fromGrid = gridApiRef.current?.getData?.() as NorthmillGridRow[] | undefined;
    if (Array.isArray(fromGrid) && fromGrid.length > 0) {
      return fromGrid;
    }
    return gridRows;
  }, [gridRows]);

  const handleOk = async () => {
    if (!syncIds.length || gridRows.length === 0) return;
    const initial = initialPayloadsRef.current;
    const initialById = new Map(initial.map((p) => [p.integrationSyncId, p]));
    const effectiveRows = resolveRowsForSave();
    const changed: NorthmillSyncEditRowSave[] = [];
    for (const row of effectiveRows) {
      const cur = rowToSavePayload(row);
      const ini = initialById.get(cur.integrationSyncId);
      if (ini && !payloadsEqual(cur, ini)) {
        changed.push(cur);
      }
    }
    if (!changed.length) {
      message.info("Inga ändringar att spara.");
      onCancel();
      return;
    }
    try {
      await onSave(changed);
      onCancel();
    } catch {
      /* onSave ska visa fel; modal stannar öppen */
    }
  };

  const multi = records.length > 1;

  return (
    <Modal
      open={open && records.length > 0}
      title={title}
      onCancel={onCancel}
      onOk={handleOk}
      afterOpenChange={(visible) => {
        if (visible && records.length) {
          resetGridFromRecords();
        }
      }}
      okText="Spara"
      destroyOnClose
      confirmLoading={confirmLoading}
      okButtonProps={{ disabled: syncIds.length === 0 }}
      width="min(96vw, 1600px)"
      centered={false}
      style={{ top: 24 }}
      styles={{ body: { paddingTop: 12, paddingBottom: 8, maxHeight: "calc(100vh - 140px)" } }}
    >
      <Flex vertical gap={12}>
        <GridModalShell>
          <ReactDataGrid
            idProperty="id"
            dataSource={gridRows}
            columns={columns}
            onEditComplete={onEditComplete}
            onReady={(computedPropsRef) => {
              gridApiRef.current = computedPropsRef.current;
            }}
            onDataSourceCacheChange={onDataSourceCacheChange}
            editable
            enableCellBulkUpdate
            cellSelection={cellSelection}
            onCellSelectionChange={onCellSelectionChange}
            enableClipboard
            copySpreadsheetCompatibleString
            activateRowOnFocus={false}
            checkboxColumn={false}
            rowIndexColumn={false}
            showColumnMenuTool={false}
            showZebraRows={false}
            style={{ minHeight: "clamp(320px, 65vh, 720px)" }}
          />
        </GridModalShell>
      </Flex>
    </Modal>
  );
}
